import { cn } from "@/lib/cn";
import type { Tone } from "./status-tone";

/**
 * Ink over a pre-mixed wash, with no border.
 *
 * The old version stacked a tinted background, a tinted border and tinted text
 * on the same hue; at 12px that reads as a fuzzy coloured smudge. Dropping the
 * border and letting the wash define the shape is both quieter and easier to
 * read — and the wash values are per-theme tokens, so a `success` badge is
 * equally legible on white and on charcoal rather than being a 12% tint that
 * vanishes on one of them.
 */
const TONES: Record<Tone, string> = {
  neutral: "bg-surface-overlay text-content-muted",
  success: "bg-success-soft text-success",
  warning: "bg-warning-soft text-warning",
  danger: "bg-danger-soft text-danger",
  info: "bg-info-soft text-info",
};

const DOTS: Record<Tone, string> = {
  neutral: "bg-content-subtle",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
};

interface BadgeProps {
  tone?: Tone;
  /** Suppresses the leading dot where the label alone is enough. */
  plain?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Status pill.
 *
 * Always renders its label as text. Colour alone would fail for the ~8% of men
 * with red/green colour blindness, and these badges carry states that matter —
 * a `quarantined` match must not be distinguishable from `verified` only by
 * hue. The leading dot is a second, non-colour cue at small sizes where the
 * wash is too faint to register as a state at all.
 *
 * Status → tone mappings live in `status-tone.ts`.
 */
export function Badge({ tone = "neutral", plain, children, className }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
        TONES[tone],
        className,
      )}
    >
      {plain ? null : (
        <span aria-hidden="true" className={cn("size-1.5 shrink-0 rounded-full", DOTS[tone])} />
      )}
      {children}
    </span>
  );
}
